/**
 * Orquestrador do sync: liga/desliga a captura e roda um ciclo drena→sobe→baixa→aplica sobre o
 * transporte Drive. É a cola de alto nível entre motor (engine/drain), persistência e transporte.
 * Ver docs/features/SYNC.md § 3 e § 9 (Fase 2b/3).
 *
 * CONCORRÊNCIA: todas as operações que tocam o estado do sync (ciclo, ligar/desligar, restore
 * consciente, bootstrap) passam por um LOCK de fila único (`withSyncLock`). Sem ele, boot + poll
 * de 45s + foreground + botão manual sobrepunham na MESMA conexão SQLite: um `enableSync`
 * concorrente limpava a `sync_guard` no meio do apply do outro (eco de mensagens re-capturadas),
 * dois ciclos consumiam o mesmo seq de segmento e o rollback de um derrubava a transação do outro.
 *
 * NÃO testável no Jest (op-sqlite + rede). O caminho de dados (captura→dreno→wire→aplicação) é
 * validado no device por lib/sync/selfTest.ts; o I/O real do Drive exige a conta Google no device.
 */

import { execute, runInTransaction } from '@/db/client';
import { DriveApiError, ensureBackupFolder } from '@/lib/googleDrive/driveClient';
import { getAccessToken } from '@/lib/googleDrive/googleAuth';
import { hasBackupPassphrase } from '@/lib/backupPassphrase';
import { wipeAllUserData } from '@/lib/devSeed';
import {
    captureSpecs,
    dropCaptureTriggers,
    installCaptureTriggers,
    withCaptureSuspended,
    type CaptureTableSpec,
} from './capture';
import { applyRemoteMessages, drainOutbox } from './drain';
import { reconcileDerivedTotals } from './derivedTotals';
import { mergeDuplicateInvoices } from './duplicateInvoices';
import { materialize } from './engine';
import { type Message, TOMBSTONE_COLUMN } from './messages';
import {
    clearPendingUpload,
    compactOplog,
    forgetSyncLineage,
    getAllCursors,
    getDeviceSeen,
    getGcBackoffUntil,
    getOrCreateDeviceId,
    getLineageMetaId,
    getSpaceResetAt,
    hasPendingSyncWork,
    isSeeded,
    loadHlc,
    loadMessages,
    loadPendingUpload,
    markSeeded,
    markSpaceReset,
    nextOwnSegmentSeq,
    resetSeedAndCursors,
    setCursor,
    setDeviceSeen,
    setGcBackoffUntil,
    setLineageMetaId,
    setNegotiatedEnvelopeVersion,
} from './persistence';
import { diffRowAgainstKnown } from './reseed';
import { SEGMENT_SCHEMA_VERSION, deserializeSegment, serializeSegment } from './wire';
import {
    deleteDeviceFile,
    deleteSegment,
    deleteSyncFile,
    downloadSegment,
    listSegments,
    listSpace,
    listSyncFiles,
    uploadDeviceFile,
    uploadSegment,
    type RemoteSegment,
} from './transport';
import { ENVELOPE_READ_MAX, deviceFileName, negotiateEnvelopeVersion, noteDevice, type KnownDevice } from './capabilities';
import type { EnvelopeVersion } from '@/lib/backupCrypto';
import {
    cachedKeyMatchesMeta,
    clearSyncKey,
    deriveAndCacheSyncKey,
    getCachedSyncKey,
    getCachedSyncMetaId,
    replaceSyncMeta,
    verifySpaceIdentity,
} from './syncKey';
import { dedupeOccurrences } from '@/db/repositories/recurringRepository';
import { repairDanglingReferences, reportDanglingRepair } from '@/db/danglingReferenceRepair';
import { useDriveBackupStore } from '@/stores/useDriveBackupStore';
import { useSyncStore } from '@/stores/useSyncStore';
import { invalidateAllQueries } from '@/lib/queryClientRef';

/**
 * O histórico do sync ainda dá a linha como viva: o último tombstone dela é falso. É o pai que
 * só não chegou (outro aparelho, lote ainda não aplicado). O reparo de referência pendurada
 * NÃO pode recriá-lo excluído: o INSERT do sync só carrega coluna não nula, então o
 * `deleted_at` do placeholder sobreviveria à linha real (ADR 0165). Sem sync, nunca é verdade.
 */
export async function isRowAliveInOplog(table: string, rowId: string): Promise<boolean> {
    const res = await execute(
        `SELECT value FROM sync_messages WHERE table_name = ? AND row_id = ? AND column_name = ?
          ORDER BY timestamp DESC LIMIT 1`,
        [table, rowId, TOMBSTONE_COLUMN],
    ).catch(() => ({ rows: [] as Record<string, unknown>[] }));
    const latest = res.rows?.[0]?.value;
    return latest !== undefined && Number(latest) === 0;
}

/** Tabelas cujo apagar-de-vez num aparelho deixa os filhos deste pendurados (ADR 0165). */
const DANGLING_PARENT_TABLES = new Set(['accounts', 'credit_cards', 'invoices', 'subcategories']);

/** Intervalo mínimo entre ciclos oportunísticos (debounce do boot/foreground/poll). Abaixo do
 * poll de 45s (app/_layout) pra o poll não cair no freio e o ciclo sair a cada ~45s. */
const MIN_SYNC_INTERVAL_MS = 30_000;

// ─── Lock de fila do sync ────────────────────────────────────

let syncLockTail: Promise<unknown> = Promise.resolve();

/**
 * A fusão de faturas duplicadas roda em todo ciclo que aplicou algo e, uma vez por processo,
 * mesmo sem nada novo: a base que já quebrou antes deste conserto (ADR 0183) não recebe lote
 * nenhum que a dispare.
 */
let duplicateInvoicesChecked = false;

/** Simula um processo novo (testes): a próxima sincronização volta a procurar fatura duplicada. */
export function resetDuplicateInvoiceCheck(): void {
    duplicateInvoicesChecked = false;
}

/** Serializa as operações de sync (FIFO). NÃO chamar de dentro de outra operação lockada —
 * deadlock; as funções internas (Inner/syncCycle) existem exatamente pra composição interna. */
function withSyncLock<T>(fn: () => Promise<T>): Promise<T> {
    const run = syncLockTail.then(fn, fn);
    syncLockTail = run.catch(() => {});
    return run;
}

export interface SyncCycleResult {
    ok: boolean;
    uploaded: number;
    applied: number;
    error?: string;
    /** `'space_reset'`: a sincronização foi redefinida em outro aparelho (ADR 0191). */
    code?: 'space_reset';
}

/**
 * O espaço na pasta não é mais o desta chave: outro aparelho redefiniu a sincronização (ADR 0191),
 * ou trocou a senha e religou. A tela do Backup reconhece ESTE texto (`lastError`) e oferece os
 * dois caminhos — entrar com a senha nova ou redefinir de novo a partir daqui.
 */
export const SYNC_SPACE_RESET_ERROR =
    'A sincronização foi redefinida em outro aparelho. Para continuar, digite neste aparelho a senha de proteção usada lá.';

// ─── Ligar / desligar ────────────────────────────────────────

/**
 * Liga o sync NESTE aparelho: instala os triggers de captura e, se ainda não semeado, emite o
 * DIFF entre o banco atual e o que o oplog já sabe (ver seedOutboxFromDatabase). Idempotente.
 */
export function enableSync(): Promise<void> {
    return withSyncLock(enableSyncInner);
}

async function enableSyncInner(): Promise<void> {
    await installCaptureTriggers(execute);
    if (!(await isSeeded())) {
        await seedOutboxFromDatabase();
        await markSeeded();
    }
}

/**
 * Limpa uma linha ÓRFÃ da `sync_guard` deixada por um processo morto no meio de um
 * `withCaptureSuspended` (o `finally` nunca rodou; op-sqlite não faz rollback — ver SYNC.md).
 *
 * Enquanto a linha existe os triggers ficam MUDOS: nenhuma escrita local entra na outbox, então
 * o aparelho para de mandar as próprias mudanças — em silêncio, sem erro em lugar nenhum. O
 * `installCaptureTriggers` já limpa, mas só é alcançado DEPOIS dos guards do `maybeSyncNow`
 * (throttle de 30s, conta desconectada, senha ausente); nesses casos a linha ficava presa até
 * um ciclo completo acontecer. Chamado no boot pra fechar essa janela.
 *
 * Roda sob o lock: fora dele, apagaria a guard de um apply em andamento e o aparelho re-emitiria
 * as mensagens remotas como se fossem locais (o eco que a guard existe pra evitar).
 */
export function healStaleCaptureGuard(): Promise<void> {
    return withSyncLock(async () => {
        await execute('DELETE FROM `sync_guard`;');
    });
}

/** Desliga o sync: remove os triggers e zera seed+cursores (um OFF→ON re-semeia). Nada é apagado. */
export function disableSync(): Promise<void> {
    return withSyncLock(async () => {
        await dropCaptureTriggers(execute);
        await resetSeedAndCursors();
    });
}

// ─── Seed / reseed ───────────────────────────────────────────

async function outboxInsert(table: string, rowId: string, column: string, value: string | number | null): Promise<void> {
    await execute(
        'INSERT INTO sync_outbox (table_name, row_id, column_name, value) VALUES (?, ?, ?, ?)',
        [table, rowId, column, value],
    );
}

/**
 * Emite pra outbox as células de UMA linha como um "nasceu agora" (tombstone falso + colunas).
 * `includeNulls`: emite também as células NULAS — necessário quando o peer pode conhecer a linha
 * com valor antigo não-nulo (restore delta): sem a mensagem de NULL, a materialização no peer
 * manteria o valor velho (célula sem mensagem não é reescrita) e ele "ressuscitaria".
 */
async function emitRowAssertion(spec: CaptureTableSpec, row: Record<string, unknown>, includeNulls = false): Promise<string> {
    const rowId = spec.rowKey.map((c) => String(row[c])).join(':');
    await outboxInsert(spec.table, rowId, TOMBSTONE_COLUMN, 0);
    for (const col of spec.columns) {
        const v = row[col];
        if (v != null) await outboxInsert(spec.table, rowId, col, v as string | number);
        else if (includeNulls) await outboxInsert(spec.table, rowId, col, null);
    }
    return rowId;
}

const capturedSpecs = captureSpecs();

/**
 * Semeia a outbox com o DIFF entre o banco atual e o oplog materializado — não com o banco
 * inteiro. A versão antiga re-semeava TUDO como "nasceu agora": no OFF→ON, cada célula ganhava
 * HLC de agora e vencia o LWW sobre qualquer edição que os peers fizeram enquanto este aparelho
 * esteve fora — edições revertidas e deleções ressuscitadas, em silêncio. Com o diff:
 *  - linha que o oplog não conhece → assertion completa (dado novo de verdade);
 *  - célula divergente do oplog → mensagem só dela (edição legítima feita com sync OFF; se o
 *    peer também editou, é conflito real e o LWW decide);
 *  - célula igual ao oplog → NADA (não briga com ninguém);
 *  - linha viva no oplog que sumiu do banco → tombstone (deleção feita com sync OFF propaga).
 * No primeiro ligar (oplog vazio) degrada pro seed completo, como antes.
 */
async function seedOutboxFromDatabase(): Promise<void> {
    const known = materialize(await loadMessages());
    await runInTransaction(async () => {
        for (const spec of capturedSpecs) {
            const res = await execute(`SELECT * FROM \`${spec.table}\``);
            const knownRows = known[spec.table] ?? {};
            const present = new Set<string>();

            for (const row of res.rows) {
                const rowId = spec.rowKey.map((c) => String(row[c])).join(':');
                present.add(rowId);
                const knownRow = knownRows[rowId];
                if (!knownRow) {
                    await emitRowAssertion(spec, row);
                    continue;
                }
                for (const cell of diffRowAgainstKnown(spec.columns, row as Record<string, unknown>, knownRow)) {
                    await outboxInsert(spec.table, rowId, cell.column, cell.value);
                }
            }

            for (const rowId of Object.keys(knownRows)) {
                if (!present.has(rowId)) await outboxInsert(spec.table, rowId, TOMBSTONE_COLUMN, 1);
            }
        }
    });
}

// ─── Restore consciente do sync ──────────────────────────────

/** Conjunto de linhas presentes por tabela (identidade = rowKey), pra diff de restore. */
export type RowSnapshot = Map<string, Set<string>>;

/** Fotografa as linhas atuais de todas as tabelas sincronizadas (antes de restaurar). */
export async function snapshotRows(): Promise<RowSnapshot> {
    const snap: RowSnapshot = new Map();
    for (const spec of capturedSpecs) {
        const cols = spec.rowKey.map((c) => `\`${c}\``).join(', ');
        const res = await execute(`SELECT ${cols} FROM \`${spec.table}\``);
        const set = new Set<string>();
        for (const row of res.rows) set.add(spec.rowKey.map((c) => String(row[c])).join(':'));
        snap.set(spec.table, set);
    }
    return snap;
}

/**
 * Após um restore (com a captura suspensa), emite o DELTA que torna o estado restaurado a verdade
 * nos peers: reafirma cada linha presente (tombstone falso + dados COM NULLs explícitos, HLC novo)
 * e TOMBSTONA cada linha que existia antes e sumiu no restore — sem isso, os peers manteriam
 * linhas que o restore removeu. O dreno normal sobe no próximo ciclo.
 */
export async function seedRestoreDelta(pre: RowSnapshot): Promise<void> {
    await runInTransaction(async () => {
        for (const spec of capturedSpecs) {
            const before = pre.get(spec.table) ?? new Set<string>();
            const now = new Set<string>();
            const res = await execute(`SELECT * FROM \`${spec.table}\``);
            for (const row of res.rows) now.add(await emitRowAssertion(spec, row, true));
            for (const rowId of before) {
                if (!now.has(rowId)) await outboxInsert(spec.table, rowId, TOMBSTONE_COLUMN, 1);
            }
        }
    });
}

/**
 * Restaura com o sync LIGADO, de forma consciente: fotografa o estado atual, roda o restore com a
 * captura SUSPENSA (o rewrite bruto não vira captura caótica) e depois emite o delta (reafirma o
 * presente + tombstona o removido). O chamador dispara runSyncNow() para propagar. Só use com sync
 * ligado — o restore vira o estado de TODOS os aparelhos.
 */
export function restoreUnderSync<T extends { success: boolean }>(doRestore: () => Promise<T>): Promise<T> {
    return withSyncLock(async () => {
        const pre = await snapshotRows();
        const result = await withCaptureSuspended(execute, doRestore);
        if (result.success) await seedRestoreDelta(pre);
        return result;
    });
}

// ─── Andamento e downloads em paralelo ───────────────────────

/** Andamento do ciclo, pra quem mostra uma tela de espera (o join da web). O app não registra nada. */
export interface SyncProgress {
    phase: 'download' | 'apply' | 'done';
    done: number;
    total: number;
}
let progressListener: ((p: SyncProgress) => void) | null = null;
export function setSyncProgressListener(listener: ((p: SyncProgress) => void) | null): void {
    progressListener = listener;
}
function reportProgress(p: SyncProgress): void {
    try {
        progressListener?.(p);
    } catch {
        // quem ouve não derruba o ciclo
    }
}

/** Downloads simultâneos de segmento. Seis cabem folgado na cota do Drive e num celular. */
const DOWNLOAD_CONCURRENCY = 6;

/** `Promise.all` com teto de simultâneos; resultados na ordem dos itens. `fn` não pode lançar. */
async function mapWithConcurrency<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
    const results: R[] = new Array(items.length);
    let next = 0;
    const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (next < items.length) {
            const i = next;
            next += 1;
            results[i] = await fn(items[i]);
        }
    });
    await Promise.all(workers);
    return results;
}

// ─── Ciclo ───────────────────────────────────────────────────

/**
 * Um ciclo de sync: drena a outbox local e sobe TUDO que está pendente de upload (inclui lotes de
 * ciclos anteriores cujo upload falhou), depois baixa os segmentos novos dos peers EM ORDEM e
 * aplica. Silencioso e idempotente — seguro pra rodar no boot/foreground.
 */
export function syncNow(): Promise<SyncCycleResult> {
    return withSyncLock(syncCycle);
}

async function syncCycle(): Promise<SyncCycleResult> {
    // Chave de sync COMPARTILHADA (não a de backup, que tem salt por aparelho). Sem ela, o sync
    // não foi configurado neste aparelho — o toggle da tela pede a senha e chama configureSyncKey.
    const key = await getCachedSyncKey();
    if (!key) return { ok: false, uploaded: 0, applied: 0, error: 'Sincronização não configurada — ligue o interruptor e digite a senha.' };

    const hlc = await loadHlc();
    const deviceId = await getOrCreateDeviceId();
    let uploaded = 0;
    let applied = 0;

    try {
        const token = await getAccessToken();
        const folderId = await ensureBackupFolder(token);

        // 0. Lista o espaço UMA vez (segmentos + arquivos de capacidade) e negocia a versão do
        // envelope com quem está nele (ADR 0181): a MENOR que os aparelhos vivos leem. Aparelho
        // conhecido = segmento listado, arquivo de capacidade, sinal de vida lembrado (`seen_*`),
        // ou cursor de antes desta versão (conta como visto agora e expira como os outros).
        // Arquivo de capacidade não existe = app antigo = só lê v1.
        const space = await listSpace(token, folderId);
        // Antes de QUALQUER upload: com a chave de antes de uma redefinição (ADR 0191), subir
        // segmento poria na pasta um arquivo que ninguém mais abre — e segmento ilegível bloqueia
        // quem o lê. Para aqui, sem drenar: a escrita local fica na outbox e no banco.
        if ((await verifySpaceIdentity(token, space.metas)) === 'reset') {
            return { ok: false, uploaded: 0, applied: 0, code: 'space_reset', error: SYNC_SPACE_RESET_ERROR };
        }
        const metaId = await getCachedSyncMetaId();
        if (metaId && metaId !== (await getLineageMetaId())) await setLineageMetaId(metaId);
        const segments = space.segments;
        const cursors = await getAllCursors(); // uma query, não uma por segmento
        const seen = await getDeviceSeen();
        const now = Date.now();
        const known = new Map<string, KnownDevice>();
        const seenUpdates = new Map<string, number>();
        const stamp = (iso: string | undefined) => { const ms = iso ? Date.parse(iso) : NaN; return Number.isFinite(ms) ? ms : now; };
        const note = (dev: string, ms: number, reads: number | null = null) => {
            if (dev === deviceId) return;
            noteDevice(known, dev, ms, reads);
            if (ms > (seen.get(dev) ?? 0) && ms > (seenUpdates.get(dev) ?? 0)) seenUpdates.set(dev, ms);
        };
        for (const seg of segments) note(seg.deviceId, stamp(seg.file.createdTime));
        for (const d of space.devices) note(d.caps.deviceId, stamp(d.file.createdTime), d.caps.envelope);
        for (const [dev, ms] of seen) note(dev, ms);
        for (const dev of cursors.keys()) if (dev !== deviceId && !known.has(dev)) note(dev, now);
        const writeVersion: EnvelopeVersion = negotiateEnvelopeVersion(known.values(), deviceId, now);
        await setNegotiatedEnvelopeVersion(writeVersion);

        // 1. Drena a outbox → oplog (pending_upload=1) e sobe TUDO pendente num segmento próprio
        // (append-only). A flag só é limpa DEPOIS do Drive aceitar: falha de rede aqui deixa o
        // lote pendente e o próximo ciclo re-sobe com os MESMOS HLCs (sem re-carimbar) — antes,
        // a outbox já tinha sido esvaziada e o lote nunca mais subia (peers divergiam pra sempre).
        await drainOutbox(hlc);
        const outgoing = await loadPendingUpload();
        if (outgoing.length > 0) {
            const seq = await nextOwnSegmentSeq();
            const own = await uploadSegment(token, folderId, deviceId, seq, serializeSegment(deviceId, seq, outgoing, key, undefined, writeVersion));
            await clearPendingUpload();
            uploaded = outgoing.length;
            // A listagem do passo 0 é de antes deste upload; a GC conta os segmentos próprios, então
            // o recém-subido entra como se tivesse sido listado (é o que uma listagem agora veria).
            segments.push(own);
        }

        // Publica a própria capacidade (um arquivo por aparelho; o nome diz o que ele lê) e
        // apaga o nome antigo quando a capacidade mudou (atualização do app). Best-effort e DEPOIS
        // do segmento: o próximo ciclo tenta de novo, e sem ele os outros só ficam escrevendo v1
        // um pouco mais — o lote do usuário vale mais que o aviso.
        const myCaps = { deviceId, envelope: ENVELOPE_READ_MAX, schema: SEGMENT_SCHEMA_VERSION };
        const myName = deviceFileName(myCaps);
        const mine = space.devices.filter((d) => d.caps.deviceId === deviceId);
        try {
            if (!mine.some((d) => d.file.name === myName)) await uploadDeviceFile(token, folderId, myCaps, new Date(now).toISOString());
            for (const d of mine) if (d.file.name !== myName) await deleteDeviceFile(token, d.file.id);
        } catch (e) {
            console.warn('[sync] publicar a capacidade deste aparelho falhou (tenta no próximo ciclo):', e);
        }


        // 2. Baixa segmentos dos peers além do cursor, EM ORDEM por aparelho (listSpace ordena
        // por device+seq). Um segmento ilegível BLOQUEIA aquele aparelho neste ciclo: nem os
        // posteriores são aplicados, nem o cursor avança — antes, um segmento corrompido era
        // pulado e um posterior legível arrastava o cursor por cima dele, perdendo aquele lote
        // PARA SEMPRE no receptor. A listagem é a do passo 0 (o próprio aparelho não se reimporta).
        // Candidatos na ordem (aparelho, seq) — o próprio aparelho não se reimporta. O DOWNLOAD é
        // paralelo (DOWNLOAD_CONCURRENCY por vez: num join com dezenas de segmentos, baixar um a um
        // gastava quase todo o tempo em ida e volta até o Drive); a APLICAÇÃO abaixo segue a ordem
        // da lista, que é o que importa pro cursor. Erro de download não derruba a fila na hora:
        // fica guardado e é tratado na vez daquele segmento, com a mesma regra de antes.
        const wanted = segments.filter(
            (seg) => seg.deviceId !== deviceId && seg.seq > (cursors.get(seg.deviceId) ?? 0),
        );
        let fetched = 0;
        reportProgress({ phase: 'download', done: 0, total: wanted.length });
        const downloads = await mapWithConcurrency(
            wanted,
            DOWNLOAD_CONCURRENCY,
            async (seg): Promise<{ envelope: string } | { error: unknown }> => {
                try {
                    return { envelope: await downloadSegment(token, seg.file.id) };
                } catch (error) {
                    return { error };
                } finally {
                    fetched += 1;
                    reportProgress({ phase: 'download', done: fetched, total: wanted.length });
                }
            },
        );
        const incoming: Message[] = [];
        const advance = new Map<string, number>();
        const blocked = new Set<string>();
        let unreadable = false;
        let vanished = false;
        for (let i = 0; i < wanted.length; i += 1) {
            const seg = wanted[i];
            const download = downloads[i];
            if (blocked.has(seg.deviceId)) continue;
            if ('error' in download) {
                // Arquivo listado que sumiu antes do download = a GC de OUTRO aparelho apagou um
                // segmento coberto entre a nossa listagem e o download. NÃO é erro do espaço:
                // bloqueia o device neste ciclo (cursor não anda, nada dele aplica) e o próximo
                // ciclo re-lista já vendo o checkpoint. Sem isto o 404 derrubava o ciclo inteiro
                // — pior no join, que acabou de wipar a base e reportaria "1ª sincronização
                // falhou" num app vazio.
                if (download.error instanceof DriveApiError && download.error.isNotFoundError) {
                    blocked.add(seg.deviceId);
                    vanished = true;
                    continue;
                }
                throw download.error;
            }
            const body = deserializeSegment(download.envelope, key);
            if (!body) {
                blocked.add(seg.deviceId);
                unreadable = true;
                continue;
            }
            // O `basis` do checkpoint nomeia todo aparelho que o compactador conhecia: é como um
            // joiner fica sabendo de um app antigo cujos segmentos a GC já apagou (ADR 0181).
            if (body.basis) for (const dev of Object.keys(body.basis)) note(dev, stamp(seg.file.createdTime));
            incoming.push(...body.messages);
            advance.set(seg.deviceId, Math.max(advance.get(seg.deviceId) ?? 0, seg.seq));
        }
        if (incoming.length > 0) {
            reportProgress({ phase: 'apply', done: wanted.length, total: wanted.length });
            // Só com alguém ouvindo: cede um tick pra tela pintar "organizando…" antes do apply, que
            // segura a thread (os awaits dele são microtasks). Sem ouvinte, nada muda no celular.
            if (progressListener) await new Promise<void>((resolve) => setTimeout(resolve, 0));
            const skipped = await applyRemoteMessages(hlc, incoming);
            if (skipped > 0) console.warn(`[sync] ${skipped} linha(s) puladas no apply (conflito de índice único)`);
            applied = incoming.length;
        }
        // Avança os cursores só DEPOIS de aplicar com sucesso (senão um erro perderia o segmento).
        // O mapa em memória acompanha — a GC abaixo decide cobertura pelos cursores PÓS-apply.
        for (const [dev, seq] of advance) {
            await setCursor(dev, seq);
            cursors.set(dev, seq);
        }
        for (const [dev, ms] of seenUpdates) await setDeviceSeen(dev, ms);

        // Fatura do mesmo cartão+mês criada em dois aparelhos (ADR 0183): a do outro foi pulada
        // no apply e as compras dele ficaram apontando pra ela. Antes do reparo de referência
        // pendurada (que contaria essas compras como `pending` pra sempre) e sob o lock, como o
        // dedupe; o que é capturado sobe no próximo ciclo. Isolado: reparo oportunístico.
        if (applied > 0 || !duplicateInvoicesChecked) {
            duplicateInvoicesChecked = true;
            try {
                const merged = await mergeDuplicateInvoices();
                if (merged.adopted + merged.relinked + merged.forecasts > 0) {
                    console.warn(`[sync] fatura duplicada: ${merged.adopted} adotada(s), ${merged.relinked} linha(s) religada(s), ${merged.forecasts} previsto(s) a mais`);
                    if (applied === 0) invalidateAllQueries();
                }
            } catch (e) {
                console.warn('[sync] fusão de faturas duplicadas falhou:', e);
            }
        }

        // Aplicou dado remoto → invalida as telas pra a UI (home, transações recentes) refletir
        // sem precisar navegar. Só quando algo mudou de fato (não a cada ciclo vazio).
        if (applied > 0) {
            // Um peer ainda no build ANTIGO segue materializando ocorrências do próprio ponteiro
            // atrasado e empurrando as duplicatas pra cá (ver dedupeOccurrences). Consertar AQUI,
            // e não nos call-sites, cobre de uma vez os quatro caminhos que aplicam dado remoto
            // — boot, poll de 45s, retorno do foreground e o pull-to-refresh da Home; pendurado
            // em dois deles, a duplicata que chegasse pelo poll ficava na tela até o próximo boot.
            //
            // Depois do apply e ainda sob o lock: a `sync_guard` já caiu (o withCaptureSuspended
            // fecha dentro do applyRemoteMessages), então os soft-deletes SÃO capturados e o
            // conserto viaja pros outros aparelhos — e nenhum apply concorrente pode abrir por
            // baixo deles. Antes do invalidate pra a tela já receber o estado limpo.
            //
            // Isolado num try/catch próprio: é reparo oportunístico, não pode derrubar um ciclo
            // que já subiu e aplicou tudo com sucesso.
            try {
                const removed = await dedupeOccurrences();
                if (removed > 0) console.warn(`[sync] ${removed} ocorrência(s) duplicada(s) removida(s)`);
            } catch (e) {
                console.warn('[sync] dedupe de ocorrências falhou:', e);
            }
            // Saldo da conta e valor da caixinha refeitos a partir do histórico que chegou
            // (ADR 0163): o last-write-wins guarda o total de UM aparelho quando os dois lançaram
            // antes de se verem. Depois do dedupe, que também mexe em lançamento; mesmo
            // isolamento, e a correção sobe no próximo ciclo como a dele.
            try {
                const fixed = await runInTransaction(() => reconcileDerivedTotals(incoming));
                if (fixed > 0) console.warn(`[sync] ${fixed} total(is) refeito(s) a partir do histórico`);
            } catch (e) {
                console.warn('[sync] recálculo dos totais falhou:', e);
            }
            // Outro aparelho apagou de vez uma conta, cartão, fatura ou subcategoria (o restore de
            // um build anterior à ADR 0165 faz isso com o excluído que o backup não levava). O
            // apply roda com FK OFF, então os lançamentos daqui ficaram apontando pro nada. Recria
            // o pai EXCLUÍDO (a subcategoria só é desligada); a escrita é capturada e o conserto
            // chega aos outros aparelhos, que têm a mesma órfã. Só quando o lote trouxe o
            // tombstone: é a única porta, e assim o ciclo comum não paga o `foreign_key_check`.
            if (incoming.some((m) => m.column === TOMBSTONE_COLUMN && Number(m.value) === 1
                && DANGLING_PARENT_TABLES.has(m.table))) {
                try {
                    const repaired = await runInTransaction(() => repairDanglingReferences(
                        (sqlText, params) => execute(sqlText, params as never),
                        { parentStillComing: isRowAliveInOplog },
                    ));
                    reportDanglingRepair(repaired, 'sync');
                } catch (e) {
                    console.warn('[sync] reparo de referência pendurada falhou:', e);
                }
            }
            invalidateAllQueries();
        }

        // Compacta o oplog local (mantém só o vencedor por célula) — sem isto o sync_messages
        // cresce sem limite. É HOUSEKEEPING isolado: vem DEPOIS do invalidate (não pode atrasar o
        // refresh da UI) e num try/catch próprio — os dados já subiram/aplicaram e os cursores já
        // andaram, então uma falha do DELETE (disco cheio/BUSY) NÃO pode virar o ciclo em erro
        // (mostraria banner de falha num sync que deu certo). Roda quando algo mudou; o DELETE
        // varre o oplog atrás dos poucos perdedores novos (custo ~O(células) — se virar hotspot em
        // base grande, compactar só as células tocadas no ciclo). Ver auditoria jul/2026 (M3).
        if (applied > 0 || uploaded > 0) {
            try {
                await runInTransaction(compactOplog);
            } catch (e) {
                console.warn('[sync] compactação do oplog falhou (ignorado, tenta no próximo ciclo):', e);
            }
        }

        // 3. Compactação/GC dos segmentos no DRIVE (M3-Drive — ADR 0040): quando a pasta acumula
        // segmentos já cobertos, sobe um CHECKPOINT (um .kseg comum com o oplog local inteiro) e
        // apaga o que ele cobre. Housekeeping como o compactOplog: try/catch próprio (os dados já
        // subiram/aplicaram — uma falha aqui NÃO pode virar o ciclo em erro) e NUNCA num ciclo
        // doente: segmento ilegível OU sumindo no meio (GC de outro aparelho em andamento) —
        // com qualquer coisa estranha no espaço, não se apaga nada.
        if (!unreadable && !vanished) {
            try {
                await compactDriveSegments(token, folderId, deviceId, key, segments, cursors, writeVersion);
            } catch (e) {
                console.warn('[sync] GC de segmentos no Drive falhou (ignorado, tenta no próximo ciclo):', e);
            }
        }

        // Segmento ilegível não pode ficar mudo (era o split-brain silencioso do "Sincronizado ✓"):
        // diagnostica com o verificador de chave do meta e reporta o erro certo.
        if (unreadable) {
            const keyOk = await cachedKeyMatchesMeta(token, folderId).catch(() => null);
            // Este aparelho redefiniu e a chave dele é a do meta: quem escreve ilegível é um
            // aparelho que ainda não entrou de novo — em geral num Kontto de antes da redefinição.
            const resetHere = keyOk !== false && (await getSpaceResetAt()) !== null;
            return {
                ok: false,
                uploaded,
                applied,
                error: keyOk === false
                    ? 'Senha de sincronização diferente entre os aparelhos. Desligue e religue o sync usando a MESMA senha em todos.'
                    : resetHere
                        ? 'Outro aparelho ainda está na sincronização de antes da redefinição. Atualize o Kontto nele e digite a senha de proteção deste aparelho.'
                        : 'Um aparelho enviou dados que este app não conseguiu ler. Verifique se o Kontto está atualizado em todos os aparelhos.',
            };
        }

        reportProgress({ phase: 'done', done: wanted.length, total: wanted.length });
        return { ok: true, uploaded, applied };
    } catch (e) {
        return { ok: false, uploaded, applied, error: describeSyncFailure(e) };
    }
}

/**
 * Falha de rede vira frase de gente. O texto cru chegava inteiro ao card do Backup e ao toast do
 * "Sincronizar agora" ("fetch failed: Fetch request has been canceled" — é o timeout de 30s do
 * driveClient abortando a requisição). O resto (senha, conta, Drive API) segue como veio: já tem
 * mensagem própria ou é raro o bastante pra valer o texto exato no relato de suporte.
 */
const NETWORK_FAILURE = /network|fetch failed|cancel+ed|timed? ?out|unable to resolve host|failed to connect|offline/i;

export function describeSyncFailure(e: unknown): string {
    const message = e instanceof Error ? e.message : String(e);
    if (NETWORK_FAILURE.test(message)) {
        return 'Sem conexão com o Google Drive. O Kontto tenta de novo sozinho quando a internet voltar.';
    }
    return message;
}

// ─── Compactação/GC dos segmentos no Drive (ADR 0040) ────────

/**
 * Nº mínimo de segmentos COBERTOS (próprios + alheios já consumidos) que dispara a compactação.
 * O ganho LÍQUIDO é garantido descontando as partes que o próprio checkpoint cria (ver o gatilho
 * na função) — sem o desconto, um oplog grande o bastante pra ≥20 partes entraria em laço
 * patológico: todo ciclo re-subiria o oplog inteiro e apagaria o checkpoint anterior, pra sempre.
 */
export const DRIVE_GC_MIN_COVERED_SEGMENTS = 20;

/** Alvo de bytes do JSON CRU por parte do checkpoint. O envelope cifra e HEX-codifica (~2×) com
 *  overhead, então ~1,5MB cru ≈ ~3,5MB no Drive — folga sob o limite multipart (~5MB). O corte é
 *  por BYTES (célula tem valor de tamanho livre): um cap por Nº de mensagens deixaria uma parte
 *  gorda estourar o limite e o upload falharia TODO ciclo — a GC nunca completaria. */
const CHECKPOINT_PART_RAW_BYTES = 1_500_000;

/** Espera após uma rodada de deleção 100% falha (ex.: 403 persistente no DELETE) antes de nova
 *  tentativa de GC — senão o gatilho segue satisfeito e cada ciclo re-sobe o checkpoint inteiro
 *  sem nunca encolher a pasta. Sucesso parcial não agenda backoff. */
const GC_FAILURE_BACKOFF_MS = 6 * 60 * 60 * 1000;

/**
 * Sobe um CHECKPOINT e apaga os segmentos que ele cobre (M3-Drive — ADR 0040).
 *
 * Um checkpoint é um `.kseg` COMUM (seq próprio, monotônico) carregando o oplog local INTEIRO —
 * o vencedor por célula, inclusive mensagens de autoria ALHEIA, com timestamps VERBATIM
 * (re-carimbar corromperia o LWW). Por ser um segmento normal, todo leitor o consome pelo fluxo
 * de sempre: app antigo de mesmo schema, joiner (`joinExistingSyncSpace` segue lendo "tudo desde
 * o cursor 0"), peer que ficou meses offline com o cursor apontando pro buraco apagado. Sem
 * manifesto de cursor, sem tipo de arquivo novo, sem política de peer morto — ver o ADR pro
 * porquê do design in-band e a prova do invariante.
 *
 * COBERTO = segmento próprio anterior ao checkpoint, ou alheio JÁ CONSUMIDO (`seq ≤ cursor`):
 * consumido ⟹ persistido no oplog local (applyRemoteMessages grava tudo ANTES de o cursor
 * avançar) ou dominado por uma mensagem que está lá — nos dois casos o conteúdo vive no
 * checkpoint. A deleção só roda DEPOIS de todas as partes confirmadas no Drive (um throw no
 * upload aborta antes de apagar qualquer coisa — checkpoint parcial é só um segmento extra
 * idempotente) e é best-effort por arquivo (404/erro individual fica pra próxima compactação).
 * Só nomes que parseiam como segmento entram na lista — meta/backups nunca são tocados.
 *
 * PRÉ-CONDIÇÕES (o chamador é o syncCycle, sob o lock): ciclo SAUDÁVEL (nenhum segmento
 * ilegível nem sumido no meio da leitura), `segments` = listagem DESTE ciclo e `cursors` já
 * refletindo os avanços do apply. Um chamador novo que pule qualquer uma delas pode apagar
 * segmento não coberto.
 */
async function compactDriveSegments(
    token: string,
    folderId: string,
    deviceId: string,
    key: Uint8Array,
    segments: RemoteSegment[],
    cursors: Map<string, number>,
    version: EnvelopeVersion,
): Promise<void> {
    // Saídas baratas: covered ⊆ segments (e todo checkpoint tem ≥1 parte), então abaixo do
    // gatilho bruto nem o banco é tocado — este caminho roda em TODO ciclo de sync.
    if (segments.length < DRIVE_GC_MIN_COVERED_SEGMENTS) return;
    if ((await getGcBackoffUntil()) > Date.now()) return;

    const covered = segments.filter(
        (s) => s.deviceId === deviceId || s.seq <= (cursors.get(s.deviceId) ?? 0),
    );
    if (covered.length < DRIVE_GC_MIN_COVERED_SEGMENTS) return;

    // Lido DEPOIS do apply/compactOplog deste ciclo: cobre tudo que os cursores (já avançados)
    // dizem que consumimos. Oplog vazio não afirma nada — e portanto não autoriza apagar nada.
    const messages = await loadMessages();
    if (messages.length === 0) return;

    // Partes cortadas por BYTES e SEMPRE em fronteira de LINHA: um leitor pode listar a pasta
    // entre o upload de duas partes, e uma linha com as células divididas materializaria pela
    // metade (coluna NOT NULL faltando → savepoint pula a linha no peer até a outra parte
    // chegar). Ordena por (tabela, linha) pra co-locar as células de cada linha.
    messages.sort((a, b) =>
        a.table < b.table ? -1 : a.table > b.table ? 1 : a.row < b.row ? -1 : a.row > b.row ? 1 : 0,
    );
    const parts: Message[][] = [[]];
    let partBytes = 0;
    for (let i = 0; i < messages.length; i += 1) {
        const m = messages[i];
        const size = JSON.stringify(m).length + 1;
        const rowBoundary = i === 0
            || messages[i - 1].table !== m.table || messages[i - 1].row !== m.row;
        if (partBytes + size > CHECKPOINT_PART_RAW_BYTES && rowBoundary && parts[parts.length - 1].length > 0) {
            parts.push([]);
            partBytes = 0;
        }
        parts[parts.length - 1].push(m);
        partBytes += size;
    }

    // GATILHO COM GANHO LÍQUIDO: as partes recém-criadas contarão como cobertas no futuro, então
    // exigimos covered ≥ partes + (K−1) — cada compactação REMOVE pelo menos K−1 arquivos e o
    // laço patológico do oplog-gigante (checkpoint de ≥K partes re-disparando todo ciclo) não
    // existe. Com 1 parte (o caso normal), é o gatilho simples covered ≥ K.
    if (covered.length < parts.length + DRIVE_GC_MIN_COVERED_SEGMENTS - 1) return;

    const seqs: number[] = [];
    for (let i = 0; i < parts.length; i += 1) seqs.push(await nextOwnSegmentSeq());

    // `basis` SÓ NA ÚLTIMA parte: além do conteúdo informativo (device → seq coberto), a
    // PRESENÇA dele marca "checkpoint completo até aqui" — um leitor futuro (auto-cura, fase 2)
    // nunca pode confiar na cobertura de um checkpoint cuja parte final não subiu. Ver wire.ts.
    const basis: Record<string, number> = Object.fromEntries(cursors);
    basis[deviceId] = seqs[0] - 1; // último seq próprio NORMAL coberto pelo checkpoint

    // Um throw no upload (rede) ABORTA antes de qualquer deleção — checkpoint parcial é só um
    // segmento extra idempotente; o próximo ciclo tenta de novo.
    for (let i = 0; i < parts.length; i += 1) {
        const partBasis = i === parts.length - 1 ? basis : undefined;
        await uploadSegment(token, folderId, deviceId, seqs[i], serializeSegment(deviceId, seqs[i], parts[i], key, partBasis, version));
    }

    // Deleção best-effort em lotes concorrentes de 5 (20+ DELETEs sequenciais segurariam o lock
    // do sync por segundos). `covered` veio da listagem PRÉ-upload, então nenhuma parte recém-
    // subida está nela. Se NENHUM delete funcionar, agenda backoff (ver GC_FAILURE_BACKOFF_MS).
    let deleted = 0;
    for (let i = 0; i < covered.length; i += 5) {
        const results = await Promise.all(
            covered.slice(i, i + 5).map((seg) => deleteSegment(token, seg.file.id).then(() => true, () => false)),
        );
        deleted += results.filter(Boolean).length;
    }
    if (deleted === 0) {
        await setGcBackoffUntil(Date.now() + GC_FAILURE_BACKOFF_MS);
        console.warn('[sync] GC no Drive: nenhuma deleção funcionou — backoff agendado');
    }
}

// ─── Bootstrap do 2º aparelho ────────────────────────────────

/** true se o espaço no Drive já tem segmentos de OUTRO aparelho (existe alguém pra "entrar"). */
export async function remoteSpaceHasSegments(): Promise<boolean> {
    const token = await getAccessToken();
    const folderId = await ensureBackupFolder(token);
    const deviceId = await getOrCreateDeviceId();
    return (await listSegments(token, folderId)).some((s) => s.deviceId !== deviceId);
}

/**
 * Impressão digital dos dados sincronizáveis: por tabela, quantas linhas existem e qual o
 * instante mais recente de criação/edição/exclusão.
 *
 * É a PROVA que acompanha o carimbo `syncedAt` do backup. O carimbo sozinho é só uma AFIRMAÇÃO
 * ("quando restaurei, a base estava contida no espaço") e ela expira na primeira escrita local —
 * e expira em SILÊNCIO, porque com o sync desligado não há triggers de captura, então
 * `hasLocalOplog()` continua false por mais que o usuário use o app. Sem a impressão digital,
 * restaurar → usar o app por dias → ligar o sync pulava a confirmação e apagava tudo que foi
 * criado depois do restore.
 *
 * `created_at`/`updated_at`/`deleted_at` cobrem os três tipos de escrita, e `COUNT(*)` fecha o
 * caso das duas tabelas sem `updated_at` (`tags`, `auto_categorization_rules`). Uma consulta só
 * (UNION ALL) — roda no restore e ao ligar o sync, dois momentos que já reescrevem o banco
 * inteiro. Ordenado em JS pra não depender da ordem de avaliação do UNION.
 *
 * Divergência é sempre resolvida pro lado SEGURO: qualquer diferença (inclusive uma escrita
 * inocente, como a materialização de recorrência num foreground) devolve a confirmação
 * destrutiva, que é o comportamento de antes — nunca um join calado.
 */
export async function localDataFingerprint(): Promise<string> {
    const selects = capturedSpecs.map((spec) => {
        const cols = new Set(spec.columns);
        const stamps = ['created_at', 'updated_at', 'deleted_at']
            .filter((c) => cols.has(c))
            .map((c) => `COALESCE(MAX(\`${c}\`), '')`);
        const stampExpr = stamps.length ? stamps.join(" || '/' || ") : "''";
        return `SELECT '${spec.table}' AS t, COUNT(*) AS n, ${stampExpr} AS s FROM \`${spec.table}\``;
    });
    const r = await execute(selects.join(' UNION ALL '));
    return r.rows
        .map((row) => `${String(row.t)}:${String(row.n)}:${String(row.s)}`)
        .sort()
        .join('|');
}

/**
 * O oplog local é de OUTRO espaço — outro aparelho redefiniu a sincronização (ADR 0191) e este a
 * religou com a senha nova (desligar e ligar, em vez do "Digitar a senha nova")? Chamar DEPOIS do
 * `configureSyncKey`. Aí ligar tem que ser um join, como a volta pelo card: semear por diff contra
 * esse histórico deixaria de fora o que este aparelho tem e o espaço novo não.
 * Sem registro de linhagem (aparelho de antes do ADR 0191) não acusa — é o comportamento de antes.
 */
export async function localOplogFromAnotherSpace(): Promise<boolean> {
    if (!(await hasLocalOplog())) return false;
    const lineage = await getLineageMetaId();
    const current = await getCachedSyncMetaId();
    return lineage !== null && current !== null && lineage !== current;
}

/** Esquece o espaço anterior sem tocar nos dados (ADR 0191) — o próximo ligar semeia a base inteira. */
export function forgetPreviousSpace(): Promise<void> {
    return withSyncLock(forgetSyncLineage);
}

/** true se este aparelho já participou de um espaço de sync (tem oplog local). */
export async function hasLocalOplog(): Promise<boolean> {
    const r = await execute('SELECT 1 FROM sync_messages LIMIT 1');
    return r.rows.length > 0;
}

/**
 * true se este aparelho tem QUALQUER dado do usuário que `joinExistingSyncSpace` destruiria —
 * o join substitui a base local INTEIRA (contas, cartões, reservas, metas, orçamentos…), não
 * só transações; gatear a confirmação destrutiva por `countTransactions()` deixava passar sem
 * aviso um aparelho com o cadastro feito mas nenhum lançamento ainda. Ignora o que o seed de
 * fábrica cria (categorias/subcategorias/settings/regras) pra não assustar instalação zerada.
 */
export async function hasLocalUserData(): Promise<boolean> {
    const tables = [
        'accounts', 'credit_cards', 'transactions', 'recurring_transactions', 'installments',
        'invoices', 'budgets', 'goals', 'financings', 'reservas', 'personal_loans', 'people',
        'tags', 'pending_notifications',
    ];
    const probe = tables.map((t) => `EXISTS(SELECT 1 FROM \`${t}\`)`).join(' OR ');
    const r = await execute(`SELECT (${probe}) AS has`);
    return Number(r.rows[0]?.has ?? 0) > 0;
}

/**
 * ENTRA num espaço de sync existente: substitui a base local pelos dados do espaço — NUNCA
 * mescla (SYNC.md regra 5; sem isto, ligar o sync num 2º aparelho com dados fazia a união dos
 * dois bancos e duplicava todo o conjunto de categorias/contas default). Destrutivo para o dado
 * local: o chamador (UI) é responsável por confirmar com o usuário antes.
 *
 * Zera a identidade de sync (deviceId novo, cursores 0 → baixa TUDO), esvazia oplog/outbox
 * (nada local pra reafirmar), APAGA os dados do usuário SEM re-semear os padrões de fábrica
 * (as categorias chegam do espaço — semear duplicaria) e roda o primeiro ciclo já dentro do lock.
 */
export function joinExistingSyncSpace(): Promise<SyncCycleResult> {
    return withSyncLock(joinInner);
}

async function joinInner(): Promise<SyncCycleResult> {
    await dropCaptureTriggers(execute);
    await wipeAllUserData();
    await execute('DELETE FROM sync_outbox');
    await execute('DELETE FROM sync_messages');
    await execute('DELETE FROM sync_state'); // deviceId/HLC/cursores zerados; a chave (SecureStore) fica
    await markSeeded(); // NÃO semeia: entrar substitui, nunca mescla
    await installCaptureTriggers(execute);
    const store = useSyncStore.getState();
    store.setEnabled(true);
    // A prova cumpriu o papel: daqui pra frente quem atesta a linhagem é o oplog local
    // (`hasLocalOplog`). Mantê-la faria um join FUTURO pular a pergunta com base numa
    // restauração antiga que não vale mais.
    store.setRestoredSnapshot(null);
    return syncCycle();
}

// ─── Redefinir a sincronização (ADR 0191) ────────────────────

/**
 * REDEFINE a sincronização a partir DESTE aparelho, com a senha de proteção dele: a saída de quem
 * não lembra mais a senha usada quando o sync foi ligado no outro aparelho (a senha não fica
 * salva, então não há como recuperá-la).
 *
 * Na pasta do Drive apaga SÓ o que é do sync — o meta primeiro (os outros aparelhos param de
 * enviar no próximo ciclo), depois segmentos e arquivos de capacidade — e sobe um meta novo. Os
 * pontos de restauração (`kontto_backup_*`) não são tocados. Aqui, esquece o espaço antigo
 * (oplog, cursores, seed) e semeia a base INTEIRA: este aparelho vira a base do espaço novo.
 *
 * Nenhum dado local é apagado neste aparelho. O que só existia no espaço antigo (o que outro
 * aparelho enviou e este nunca recebeu) continua nos outros aparelhos e volta pelo backup forçado
 * de quando eles entram de novo — por isso a tela recomenda redefinir no aparelho mais completo.
 *
 * Falha no meio (rede): o meta antigo pode já ter sumido. Os outros aparelhos então param de
 * enviar (é o lado seguro) e este repete a operação, que é idempotente.
 */
export function resetSyncSpace(passphrase: string): Promise<SyncCycleResult> {
    return withSyncLock(async () => {
        const token = await getAccessToken();
        const folderId = await ensureBackupFolder(token);
        const files = await listSyncFiles(token, folderId);
        for (const meta of files.metas) await deleteSyncFile(token, meta.id);
        await mapWithConcurrency(files.others, DOWNLOAD_CONCURRENCY, (file) => deleteSyncFile(token, file.id));
        await replaceSyncMeta(passphrase, token, folderId);

        await forgetSyncLineage();
        await markSpaceReset(Date.now());
        await enableSyncInner(); // oplog vazio → seed completo
        const store = useSyncStore.getState();
        store.setEnabled(true);
        store.setRestoredSnapshot(null);
        return syncCycle();
    });
}

/**
 * Volta para a sincronização depois que OUTRO aparelho a redefiniu. Pré-condição: a chave nova já
 * foi configurada (`configureSyncKey` com a senha usada lá) e a tela já confirmou a substituição.
 *
 * Entrar de novo é um JOIN (SYNC.md regra 5 — substitui, nunca mescla): o oplog local é do espaço
 * antigo, e mesclar por ele deixaria de fora tudo que este aparelho tem e a base nova não. Antes,
 * apaga da pasta os arquivos que ESTE aparelho subiu com a chave antiga (um app de antes da
 * redefinição segue enviando até atualizar): ilegíveis para todos, eles bloqueariam o aparelho
 * nos outros para sempre — o join troca o deviceId, e ninguém mais os apagaria.
 */
export function rejoinAfterSpaceReset(): Promise<SyncCycleResult> {
    return withSyncLock(async () => {
        const token = await getAccessToken();
        const folderId = await ensureBackupFolder(token);
        const deviceId = await getOrCreateDeviceId();
        const space = await listSpace(token, folderId);
        const mine = [
            ...space.segments.filter((seg) => seg.deviceId === deviceId).map((seg) => seg.file),
            ...space.devices.filter((d) => d.caps.deviceId === deviceId).map((d) => d.file),
        ];
        await mapWithConcurrency(mine, DOWNLOAD_CONCURRENCY, (file) => deleteSyncFile(token, file.id));
        return joinInner();
    });
}

// ─── Wrappers cientes do store (UI + boot) ───────────────────

/**
 * Configura a chave de sync COMPARTILHADA a partir da senha E2E (deriva do salt compartilhado do
 * Drive e cacheia). Chamado ao LIGAR o sync — precisa da senha porque só a chave de backup fica
 * salva, e ela tem salt por aparelho. Exige conta Google conectada. LANÇA se o espaço já tem um
 * verificador de chave e a senha não bate (ver syncKey.ts).
 */
export async function configureSyncKey(passphrase: string): Promise<void> {
    const token = await getAccessToken();
    const folderId = await ensureBackupFolder(token);
    await deriveAndCacheSyncKey(passphrase, token, folderId);
}

/** Liga/desliga o sync: instala/remove a captura e reflete no store. Desligar limpa a chave. */
export async function setSyncEnabled(on: boolean): Promise<void> {
    if (on) {
        await enableSync();
        useSyncStore.getState().setEnabled(true);
    } else {
        await disableSync();
        await clearSyncKey();
        const store = useSyncStore.getState();
        store.setEnabled(false);
        // Desligar corta a linhagem: o usuário volta a editar sem captura, e a base local deixa
        // de estar contida no espaço. A prova não pode sobreviver a isso.
        store.setRestoredSnapshot(null);
    }
}

/** Ciclo de sync disparado pela UI ("Sincronizar agora"): roda e reflete o resultado no store. */
export async function runSyncNow(): Promise<SyncCycleResult> {
    const result = await syncNow();
    const store = useSyncStore.getState();
    if (result.ok) store.markSynced();
    else store.setError(result.error ?? 'Falha ao sincronizar');
    return result;
}

/**
 * Roda um ciclo de sync ANTES de o backup tirar o snapshot, e devolve o instante em que o espaço
 * ficou em dia (ou null se não deu). Chamado por `runDriveBackup`/`runFolderBackup`.
 *
 * Por que ANTES e não depois: o problema que isto resolve é um backup existir com linha que o
 * espaço de sync não tem. Sincronizando primeiro, tudo que é local sobe e tudo que os peers
 * mandaram desce — só então o snapshot é tirado, e ele nasce contido no espaço. Rodando depois,
 * um upload que falhasse deixava exatamente a divergência que se queria evitar.
 *
 * Isso importa porque backup e sync são LINHAGENS INDEPENDENTES na mesma pasta do Drive: o
 * snapshot é um estado, o espaço é um oplog, e nada os reconciliava. Um backup mais novo que o
 * espaço fazia com que restaurar e depois ligar o sync trocasse o dado restaurado pelo estado
 * mais antigo do oplog — perda silenciosa. Em compasso, "substituir pelo que está no sync"
 * devolve o mesmo dado.
 *
 * Devolve null (sem carimbo) em vez de lançar: um backup SEMPRE tem que acontecer, mesmo sem
 * rede. Sem carimbo, o app volta a tratar o snapshot como possivelmente divergente e pergunta.
 */
export async function syncBeforeSnapshot(): Promise<string | null> {
    const sync = useSyncStore.getState();
    if (!sync.enabled) return null;
    if (!useDriveBackupStore.getState().connectedEmail) return null;
    try {
        // Sem chave configurada NESTE aparelho (ex.: reinstalado) não há ciclo possível.
        if (!(await getCachedSyncKey())) return null;
        await enableSync(); // idempotente: garante triggers + seed
        const result = await syncNow();
        if (!result.ok) {
            sync.setError(result.error ?? 'Falha ao sincronizar');
            return null;
        }
        useSyncStore.getState().markSynced();
        return useSyncStore.getState().lastSyncAt;
    } catch (e) {
        console.warn('[sync] ciclo pré-backup falhou:', e);
        return null;
    }
}

/**
 * Sync ligado, mas sem a senha ou a chave NESTE aparelho: trocou de aparelho restaurando o backup
 * do sistema (a flag `enabled` vem junto; o Keychain/Keystore, não) ou reinstalou. Nenhum ciclo
 * roda até religar — e antes isso era um `return` mudo: o aparelho parava de sincronizar pra sempre
 * sem nada na tela. A tela do Backup reconhece ESTA constante (`lastError`) e troca o "Tentar de
 * novo", que falharia igual, por "Digitar a senha" (ADR 0193).
 */
export const SYNC_NOT_CONFIGURED_HERE =
    'A sincronização precisa ser configurada de novo neste aparelho. Desligue e ligue de novo, com a mesma senha dos outros aparelhos.';

/**
 * Ciclos oportunísticos rodando ou esperando o lock. Um rodando + um na fila já cobre qualquer
 * pedido novo: o da fila ainda não começou, então vai drenar a escrita e baixar o que chegar até
 * lá. Sem o teto, puxar a Home várias vezes com a rede ruim empilhava um ciclo de até 30s
 * (timeout do driveClient) por puxão, e a volta da rede ainda esperava a fila inteira.
 */
let opportunisticInFlight = 0;
const MAX_OPPORTUNISTIC_IN_FLIGHT = 2;

export interface MaybeSyncOptions {
    /**
     * Ignora o intervalo mínimo entre ciclos. Para pedido EXPLÍCITO (puxar a Home pra baixo) e
     * para escrita local esperando pra subir — é justamente nesses dois que o freio de 30s
     * atrapalhava: o gesto virava no-op silencioso se o poll tinha rodado há pouco.
     */
    force?: boolean;
    /** Só roda se houver escrita local esperando pra subir (ver `hasPendingSyncWork`). */
    onlyIfPending?: boolean;
}

/**
 * Ciclo OPORTUNÍSTICO (boot/foreground/poll/puxar a Home/pós-escrita). Silencioso e guardado: só
 * roda se ligado, Pro, conectado, com senha, e — salvo `force` — passado o intervalo mínimo desde
 * o último. Garante os triggers instalados (idempotente) — eles vivem no .db e sobrevivem a
 * reboots, mas isto cobre um device recém-restaurado.
 */
export async function maybeSyncNow(isPro: boolean, opts: MaybeSyncOptions = {}): Promise<void> {
    const sync = useSyncStore.getState();
    if (!sync.enabled || !isPro) return;
    if (!useDriveBackupStore.getState().connectedEmail) return;
    if (!opts.force && sync.lastSyncAt && Date.now() - new Date(sync.lastSyncAt).getTime() < MIN_SYNC_INTERVAL_MS) return;

    try {
        // As leituras de SecureStore ficam DENTRO do try: no iOS, acordar com o
        // aparelho ainda bloqueado dá "User interaction is not allowed" no Keychain,
        // e os callers disparam isto com `void` — fora do try viraria unhandled
        // rejection (visto no Sentry). Falhou → pula o ciclo; o próximo tenta de novo.
        if (!(await hasBackupPassphrase()) || !(await getCachedSyncKey())) {
            sync.setError(SYNC_NOT_CONFIGURED_HERE);
            return;
        }
        if (opts.onlyIfPending && !(await hasPendingSyncWork())) return;
        if (opportunisticInFlight >= MAX_OPPORTUNISTIC_IN_FLIGHT) return;
        opportunisticInFlight += 1;
        try {
            await enableSync(); // idempotente: garante triggers + seed
            const result = await syncNow();
            if (result.ok) sync.markSynced();
            else {
                sync.setError(result.error ?? 'Falha ao sincronizar');
                console.warn('[sync] ciclo oportunístico falhou:', result.error);
            }
        } finally {
            opportunisticInFlight -= 1;
        }
    } catch (e) {
        console.warn('[sync] ciclo oportunístico falhou:', e);
    }
}

// ─── Envio logo depois de uma escrita ────────────────────────

/**
 * Espera depois da ÚLTIMA escrita antes de subir: salvar uma transação costuma vir em rajada
 * (a linha, as tags, a fatura recalculada), e a janela junta tudo num ciclo — e num segmento — só.
 */
const AFTER_WRITE_DELAY_MS = 3_000;

let afterWriteTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Agenda o envio do que acabou de ser escrito (SYNC.md § Transporte, cadência "pós-escrita").
 * Sem isto, uma despesa lançada no celular só subia no próximo poll de 45s — e, se o app fosse
 * fechado antes, só quando fosse aberto de novo. O outro aparelho puxava e não vinha nada, e o
 * único jeito de destravar era achar "Sincronizar agora" no Backup.
 *
 * Chamado a cada mutação bem-sucedida (MutationCache do app/_layout). Sem sync ligado é um `if`
 * e nada mais. Mutação que não mexe em tabela sincronizada não gasta rede: o ciclo só sai com
 * escrita pendente (`onlyIfPending`).
 */
export function scheduleSyncAfterWrite(isPro: boolean): void {
    if (!isPro || !useSyncStore.getState().enabled) return;
    if (afterWriteTimer) clearTimeout(afterWriteTimer);
    afterWriteTimer = setTimeout(() => {
        afterWriteTimer = null;
        void runAfterWriteSync(isPro);
    }, AFTER_WRITE_DELAY_MS);
}

/**
 * Antecipa o envio agendado — chamado quando o app SAI da frente. Lançar e fechar o app em menos
 * de 3s é o caso comum; esperar o timer seria perder a corrida pro sistema suspender o JS (no iOS
 * sobram poucos segundos). Se o upload for cortado no meio, nada se perde: `pending_upload` só é
 * limpo depois de o Drive aceitar, e o próximo ciclo re-sobe o lote com os mesmos HLCs.
 * Sem envio agendado, não faz nada.
 */
export function flushSyncAfterWrite(isPro: boolean): Promise<void> {
    if (!afterWriteTimer) return Promise.resolve();
    clearTimeout(afterWriteTimer);
    afterWriteTimer = null;
    return runAfterWriteSync(isPro);
}

function runAfterWriteSync(isPro: boolean): Promise<void> {
    // Ninguém espera esta promise; o catch é a rede de segurança contra unhandled rejection.
    return maybeSyncNow(isPro, { force: true, onlyIfPending: true }).catch((e) =>
        console.warn('[sync] envio pós-escrita falhou:', e),
    );
}
